import * as z from "zod";
import type { ApiContext } from "../context";
import {
  type ApiNotAuthorized,
  ErrorCode,
  InvalidFormField,
  StoreNotFound,
  type StoreRoleForbidden,
  UnexpectedApiError,
  ValidationError,
  WrongCommerceMode,
} from "../errors";
import { parse } from "../model/common";
import { Store } from "../model/store";
import { forward, type Result } from "../result";
import type { Transport } from "../transport";
import { asRoleForbidden } from "./role";

const ConnectUrl = z.object({ url: z.string() });
const CurrentStore = z.object({ store: Store.nullable() });
const Disconnected = z.object({ success: z.boolean() });
const PricesPushed = z.object({ updated: z.number() });

/** One variant's new price, as a decimal string — Shopify's own format, not cents. */
export interface VariantPriceUpdate {
  shopifyVariantId: string;
  price: string;
}

/**
 * `/providers/shopify`: the Shopify connection itself, as opposed to the
 * provider-agnostic routes under `/stores/:storeId/providers`.
 *
 * Only what a browser starts. The OAuth callback and the webhook endpoints under
 * this prefix are Shopify calling us, never a client, so they are deliberately
 * absent — `oauth/callback` answers with redirects and plain text and is marked
 * `@ApiExcludeEndpoint()` on the backend.
 *
 * Note the split with `StoresApi`: `disconnectProvider` there is the key-driven
 * route that works for any provider, while `disconnect` here is Shopify's own.
 * Both exist on the backend; prefer the provider-agnostic one for new work.
 */
export class ShopifyApi {
  constructor(private readonly t: Transport) {}

  /**
   * Starts the **legacy** OAuth install and returns the URL to send the merchant
   * to. An install begun from the Shopify admin uses managed installation and
   * token exchange instead, and never comes through here.
   *
   * `mobile` switches the callback to the app's custom scheme.
   *
   * Only half-covered, and the other half is not addable: completing an install
   * needs Shopify to redirect back. `shopify-connection.test.ts` asserts the URL
   * this hands out and the ownership check in front of it; nothing exercises the
   * callback.
   */
  async connect(
    ctx: ApiContext,
    storeId: string,
    shop: string,
    opts: { mobile?: boolean } = {},
  ): Promise<
    Result<string, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField | WrongCommerceMode>
  > {
    const r = await this.t.post(ctx, "/providers/shopify/connect", {
      body: { storeId, shop, mobile: opts.mobile },
    });
    return forward(
      r,
      (j) => parse(ConnectUrl, j).url,
      (e) => {
        if (e instanceof ValidationError) {
          if (e.code === ErrorCode.WrongCommerceMode)
            return new WrongCommerceMode(e.incorrectValue as "SHOPIFY" | "STRIPE", e.detailedDesc, e);
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /**
   * The store the caller's context resolves to, or null.
   *
   * What the embedded admin opens with: a Shopify session token names a shop, so
   * there is no store to pick. A Firebase caller gets their first linked store
   * instead, which is why the merchant SPA can call it too.
   *
   * Also refreshes the stored Shopify access token as a side effect, which is
   * what recovers a revoked or stale one without a reinstall.
   */
  async currentStore(ctx: ApiContext): Promise<Result<Store | null, ApiNotAuthorized>> {
    const r = await this.t.get(ctx, "/providers/shopify/current-store");
    return forward(
      r,
      (j) => parse(CurrentStore, j).store,
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /** Drops the store's Shopify connection. The Shopify app itself stays installed. */
  async disconnect(
    ctx: ApiContext,
    storeId: string,
  ): Promise<Result<void, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden>> {
    const r = await this.t.delete(ctx, `/providers/shopify/stores/${storeId}/connection`);
    return forward(
      r,
      (j) => {
        parse(Disconnected, j);
      },
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /**
   * Pushes prices onto Shopify variants and answers how many moved.
   *
   * The count can be lower than the number of updates: a variant Shopify no
   * longer has is skipped rather than failing the batch.
   *
   * TODO: the success path has no e2e coverage and cannot easily get one — it
   * needs a real Shopify store to push to, and `utils/shopify.ts` fabricates the
   * connection row without a shop behind it. `shopify-connection.test.ts` covers
   * the refusal, which is the half that guards a merchant's live catalogue.
   *
   * Note the ordering it documents: the body is validated before the caller, so
   * an empty `updates` is a bad field rather than `StoreNotFound`.
   */
  async pushVariantPrices(
    ctx: ApiContext,
    storeId: string,
    updates: VariantPriceUpdate[],
  ): Promise<Result<number, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField>> {
    const r = await this.t.patch(ctx, `/providers/shopify/stores/${storeId}/variants/prices`, { body: { updates } });
    return forward(
      r,
      (j) => parse(PricesPushed, j).updated,
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }
}
